/**
 * App-level currency formatting utilities.
 */

import { formatLargeNumber } from '../numbers/numbers.js';
import {
    addSpaceAfterCurrencySymbol,
    formatCurrencyAmount,
    getCurrencyFormattingContext,
    getCurrencySymbolPositionFromSample,
    replaceCurrencyGlyph,
    resetCurrencyFormattingContext,
    setCurrencyFormattingContext,
} from './currency-intl.js';
import { getStringAsCurrency } from './currency.js';

const createDefaultLargeAmountFormatter = () => {
    return (number, useCurrency, currencySymbol) => {
        const { locale } = getCurrencyFormattingContext();
        const finalValue = formatLargeNumber({
            number,
            locale,
        });

        if (useCurrency) {
            return getStringAsCurrency(finalValue, currencySymbol);
        }

        return finalValue;
    };
};

let formatLargeAmountCurrencyImpl = createDefaultLargeAmountFormatter();

/**
 * @param {(number: number, useCurrency: boolean, currencySymbol: string) => string} formatter
 */
export const configureFormatLargeAmountCurrency = (formatter) => {
    formatLargeAmountCurrencyImpl = formatter;
};

export const setupCurrencyLocaleUser = (locale, currencyISO = '', currencySymbol = '') => {
    setCurrencyFormattingContext({
        locale,
        defaultCurrencyISO: currencyISO,
        defaultUserCurrencySymbol: currencySymbol,
    });
};

const adaptFormat = (number, showZero = false) => {
    try {
        if (!number && number !== 0) return;
        if (number === 0 && !showZero) return;
        let numberCurrency = number;
        if (typeof numberCurrency === 'string') {
            numberCurrency = numberCurrency.replace(/\./g, '');
            numberCurrency = numberCurrency.replace(',', '.');
            const trueNumber = Number(numberCurrency);
            if (isNaN(trueNumber)) return number;
            if (!showZero) numberCurrency = trueNumber;
        }

        const numberArray = numberCurrency.toString().split('.');
        const precision = numberArray.length === 2 ? numberArray[1].length : 0;
        return { value: Number(numberArray.join('')), precision };
    } catch (exception) {
        console.error(exception);
        return number;
    }
};

export const getNumberAsCurrency = (
    number,
    currencySymbol = '',
    showZero = false,
    removeSymbol = false,
    currencyName,
) => {
    try {
        if ([undefined, null, ''].includes(number)) return;
        if (number >= 1e12) {
            return formatLargeAmountCurrencyImpl(number, true, currencySymbol);
        }
        const adaptedFormat = adaptFormat(number, showZero);
        if (!adaptedFormat || typeof adaptedFormat !== 'object') return number;
        const { value, precision } = adaptedFormat;
        const { defaultUserCurrencySymbol, locale } = getCurrencyFormattingContext();
        const symbol = currencySymbol || defaultUserCurrencySymbol;
        let numberCurrency = formatCurrencyAmount({
            amount: value,
            precision,
            currency: currencyName || 'EUR',
            locale,
        });

        numberCurrency = replaceCurrencyGlyph(numberCurrency, symbol);

        if (removeSymbol) {
            numberCurrency = numberCurrency.replace(symbol, '').split();
        }

        numberCurrency = addSpaceAfterCurrencySymbol(numberCurrency, symbol);

        return numberCurrency;
    } catch (exception) {
        console.error(exception);
        return number;
    }
};

export const getCurrencySymbolPosition = () => {
    const { locale, defaultCurrencyISO } = getCurrencyFormattingContext();

    return getCurrencySymbolPositionFromSample(defaultCurrencyISO || 'EUR', locale);
};

export const getCurrency = ({ number, currencySymbol, precision = 2, locale = undefined }) => {
    const currencyString = number.toLocaleString(locale, {
        useGrouping: true,
        minimumFractionDigits: 2,
        maximumFractionDigits: precision,
    });
    return getStringAsCurrency(currencyString, currencySymbol, undefined, locale);
};

/** @internal Resets module state between tests. */
export const resetCurrencyAppState = () => {
    resetCurrencyFormattingContext();
    formatLargeAmountCurrencyImpl = createDefaultLargeAmountFormatter();
};
